import { GoogleOAuthProvider } from '@react-oauth/google'
import type { GetProps } from 'antd'
import { Button, Form, Input, message, QRCode, Space, Typography } from 'antd'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Logo from 'assets/icon-svg/logo.svg'
import { GOOGLE_CLIENT_ID, SESSION_KEY } from 'constants/index'
import { logInfo } from 'utils/logger'
import { isValidPassword } from 'utils/validate-fields'

type OTPProps = GetProps<typeof Input.OTP>

const { Title } = Typography

const LoginPage = () => {
  const [form] = Form.useForm()
  const [containQRCode, setContainQRCode] = useState<string>()
  const [isError401, setIsError401] = useState(false)
  const [isShowQR, setIsShowQR] = useState(true)
  const [isChangePassword, setIsChangePassword] = useState(false)

  const onChange: OTPProps['onChange'] = (text) => {
    logInfo('onChange:', text)
  }

  const onInput: OTPProps['onInput'] = (value) => {
    logInfo('onInput:', value)
  }

  const sharedProps: OTPProps = {
    onChange,
    onInput,
  }

  const navigate = useNavigate()

  const handleSubmit = async (values: {
    userName: string
    password: string
    otp: any
    newPassword: string
  }) => {
    try {
      const response = await AppAdmin.login(values)
      if (response.data?.code === 200) {
        const data = await response.data.data

        sessionStorage.setItem(SESSION_KEY.TOKEN, data.accessToken)
        sessionStorage.setItem(SESSION_KEY.USER, data.userName)
        setIsError401(false)
        message.success('Login successful!')
        navigate('/')
      } else if (response.data?.code === 201) {
        message.success('Password changed successfully. Please log in again.')
        setTimeout(() => {
          navigate(0)
        }, 1000)
      }
    } catch (error: any) {
      const responseMessage = error.response?.data?.message as string | undefined
      const isOtpStep =
        responseMessage === 'Please input OTP' || responseMessage === 'OTP Invalid'

      if (error.status === 401 && isOtpStep) {
        message.error(responseMessage)
        if (error.response.data.data) {
          setIsError401(true)
          setContainQRCode(error.response.data.data.rqCodeImage)
        } else if (error.response.data.data === null || error.response.data.data === undefined) {
          setIsError401(true)
          setIsShowQR(false)
        }
      } else if (error.status === 401) {
        message.error(responseMessage ?? 'Authentication failed')
      } else if (error.status === 429) {
        message.error(responseMessage ?? 'Too many failed attempts. Please try again later.')
      } else if (error.status === 303) {
        setIsChangePassword(true)
      } else {
        message.error(error.response.data.message)
      }
    }
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div
        style={{
          maxWidth: 400,
          margin: '50px auto 50px',
          padding: '20px',
          border: '1px solid #d9d9d9',
        }}
      >
        <Title level={2}>Login</Title>

        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            name="userName"
            label="Username"
            rules={[
              {
                required: true,
                message: 'Please input your username!',
              },
            ]}
          >
            <Input placeholder="Enter your username" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              {
                required: true,
                message: 'Please input your password!',
              },
            ]}
          >
            <Input.Password placeholder="Enter your password" />
          </Form.Item>
          {isChangePassword && (
            <>
              <Typography.Text type="warning" style={{ textAlign: 'left' }}>
                Because this is the first time you log in, you need to change the password to
                continue using the system. Please enter your new password below.
              </Typography.Text>
              <Form.Item
                name="newPassword"
                label="New password"
                rules={[
                  {
                    required: true,
                    message: 'Please input your new password!',
                  },
                  {
                    validator(_, value) {
                      if (value === form.getFieldValue('password')) {
                        return Promise.reject(
                          'The new password cannot be the same as the old password!'
                        )
                      }

                      if (isValidPassword(value) === false) {
                        return Promise.reject(
                          'Passwords must be 8-256 characters long and contain at least one number, one lowercase letter, one uppercase letter, and one special character.'
                        )
                      }

                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <Input.Password placeholder="Enter your new password" />
              </Form.Item>
              <Form.Item
                name="newPasswordAgain"
                label="New password again"
                rules={[
                  {
                    required: true,
                    message: 'Please input your new password again!',
                  },
                  {
                    validator(_, value) {
                      if (value === form.getFieldValue('newPassword')) {
                        return Promise.resolve()
                      }

                      return Promise.reject(
                        new Error('This password does not match the new password.')
                      )
                    },
                  },
                ]}
              >
                <Input.Password placeholder="Enter your new password again" />
              </Form.Item>
            </>
          )}

          {isError401 && (
            <Form.Item name="otp" label="Google Authenticator">
              <Input.OTP {...sharedProps} />
            </Form.Item>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {isChangePassword ? 'Confirm new password' : 'Login'}
            </Button>
          </Form.Item>
        </Form>

        {containQRCode && isShowQR && (
          <>
            <Space direction="vertical" align="center">
              <QRCode errorLevel="H" value={containQRCode} icon={Logo} />
            </Space>
            <br />
            <div
              style={{
                fontSize: '14px !important',
                textAlign: 'left',
                marginTop: 20,
              }}
            >
              <i>
                1. Install Google Authenticator on your phone, download:{' '}
                <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2">
                  Android
                </a>{' '}
                or{' '}
                <a href="https://itunes.apple.com/app/google-authenticator/id388497605">
                  Iphone, iPad and iPod
                </a>
                <br />
                2. Open the Google Authenticator app.
                <br />
                3. Tab menu, then tab &quot;Set up Account&quot;, then &quot;Scan a barcode&quot;.
                <br /> 4. Your phone will now be in &quot;scanning&quot; mode. When you are in this
                mode, scan the barcode below.
              </i>
            </div>
          </>
        )}
        {/* <div style={{ textAlign: "center", margin: "20px 0" }}>
          <span>or</span>
        </div>

        <GoogleLogin
          onSuccess={handleLoginSuccess}
          onError={handleLoginFailure}
          width={"1000"}
          locale="en-US"
        /> */}
      </div>
    </GoogleOAuthProvider>
  )
}

export default LoginPage
